import React from "react";
import { Link } from "react-router-dom";

/**
 * Phase 7a — Privacy placeholder.
 *
 * Short-form placeholder while legal review is pending. The commercial
 * data-handling constraints RecoveryAssure operates under today are
 * summarised below. Full policy will replace this page before Phase 7 goes
 * to a first commercial customer.
 */
export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="privacy-page">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-widest">
              RA
            </span>
            <span className="text-sm font-semibold">RecoveryAssure</span>
          </Link>
          <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 underline">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-12 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Privacy
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">
            Privacy summary
          </h1>
          <p className="mt-3 text-xs text-slate-500 italic">
            Legal review pending — a full privacy policy will replace this page
            before any commercial engagement.
          </p>
        </div>

        <section className="text-sm text-slate-700 leading-relaxed space-y-3">
          <p>
            RecoveryAssure processes Business Impact Analysis (BIA) data,
            application catalogues, supplier registers, and DR test records
            that you upload. All uploads are stored inside your workspace and
            are visible only to members you invite.
          </p>
          <p>
            <strong>What we store.</strong> Canonical datasets, analysis runs,
            findings, audit receipts, cached AI responses, remediations, and
            comparison results — all keyed by workspace id. Historical runs are
            immutable once completed.
          </p>
          <p>
            <strong>What we do not do.</strong> We do not sell customer data.
            We do not use workspace data to train shared models. We do not
            share data across workspaces. Every request to the app carries
            explicit tenant scoping enforced at the query layer.
          </p>
          <p>
            <strong>AI processing.</strong> Where AI features are invoked,
            structured prompts (containing anonymised finding facts) are sent
            to the configured LLM provider. Responses are cached per-workspace
            and never used to influence other tenants' analyses. Every AI
            output is labelled and constrained by deterministic guardrails.
          </p>
          <p>
            <strong>Retention.</strong> Workspace data is retained for the life
            of the workspace. Deletion requests can be submitted to{" "}
            <a
              href="mailto:privacy@recoveryassure.com"
              className="underline text-slate-900"
            >
              privacy@recoveryassure.com
            </a>
            .
          </p>
        </section>

        <div className="pt-6 border-t border-slate-200 text-xs text-slate-500">
          Return to{" "}
          <Link to="/" className="underline text-slate-700">home</Link>
          .
        </div>
      </main>
    </div>
  );
}
